import { apiUrl } from './api-base';
import {
  SCORING_POLL_MS,
  scoringWebSocketUrl,
  shouldPollLeaderboard,
  type PublicLeaderboard,
} from './shooting';

export type ScoreboardConnection = 'connecting' | 'open' | 'polling';
type Callbacks = {
  onBoard: (board: PublicLeaderboard) => void;
  onError: (error: string | null) => void;
  onNotFound: () => void;
  onConnection: (connection: ScoreboardConnection) => void;
};

// Vite turns this keys-only glob into filenames: no score data enters the JavaScript bundle.
const staticFiles = new Set(
  Object.keys(import.meta.glob('/public/results/*.json')),
);

const record = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);
const scoreLine = (
  value: unknown,
  id: string,
): value is Record<string, unknown> =>
  record(value) &&
  typeof value[id] === 'string' &&
  typeof value.name === 'string' &&
  typeof value.totalDisplay === 'string';
const validShots = (shots: unknown) =>
  shots == null ||
  (Array.isArray(shots) &&
    shots.every(
      (shot) =>
        record(shot) &&
        Number.isInteger(shot.shotNumber) &&
        typeof shot.pointsDisplay === 'string',
    ));
const validTeams = (round: unknown) =>
  round == null ||
  (record(round) &&
    Array.isArray(round.teams) &&
    round.teams.every(
      (team) =>
        scoreLine(team, 'teamId') &&
        typeof team.rank === 'number' &&
        Array.isArray(team.members) &&
        team.members.every(
          (member) =>
            scoreLine(member, 'shooterId') && validShots(member.shots),
        ),
    ));

function isFinalSnapshot(
  value: unknown,
  slug: string,
): value is PublicLeaderboard {
  const board = value as PublicLeaderboard | null;
  return (
    board?.ok === true &&
    board.tournament?.slug === slug &&
    board.tournament.status === 'final' &&
    typeof board.tournament.title === 'string' &&
    Number.isInteger(board.tournament.shotsPlanned) &&
    board.tournament.shotsPlanned > 0 &&
    Number.isInteger(board.tournament.shotsCounted) &&
    board.tournament.shotsCounted > 0 &&
    board.tournament.shotsCounted <= board.tournament.shotsPlanned &&
    Array.isArray(board.rows) &&
    board.rows.every(
      (row) =>
        scoreLine(row, 'shooterId') &&
        typeof row.rank === 'number' &&
        validShots(row.shots) &&
        (row.bib == null || typeof row.bib === 'string') &&
        (row.teamName == null || typeof row.teamName === 'string') &&
        (row.latest == null ||
          (record(row.latest) &&
            typeof row.latest.pointsDisplay === 'string' &&
            typeof row.latest.label === 'string')),
    ) &&
    validTeams(board.round1) &&
    validTeams(board.round2) &&
    (board.round2 == null ||
      (Number.isInteger(board.round2.shotsPlanned) &&
        board.round2.shotsPlanned > 0 &&
        Number.isInteger(board.round2.shotsCounted) &&
        board.round2.shotsCounted > 0 &&
        board.round2.shotsCounted <= board.round2.shotsPlanned))
  );
}

/** Static finals never start live transport. Every other board retains the existing live path. */
export function subscribeScoreboard(
  slug: string,
  callbacks: Callbacks,
): () => void {
  let socket: WebSocket | null = null;
  let pollTimer: number | null = null;
  let reconnectTimer: number | null = null;
  let attempt = 0;
  let stopped = false;
  const controller = new AbortController();

  const pull = async () => {
    try {
      const res = await fetch(
        apiUrl(`/api/public/shooting/${encodeURIComponent(slug)}`),
        {
          cache: 'no-store',
          credentials: 'omit',
          signal: controller.signal,
        },
      );
      if (stopped) return;
      if (res.status === 404) {
        shutdown();
        callbacks.onNotFound();
        return;
      }
      if (!res.ok) {
        callbacks.onError('The scores could not be loaded.');
        return;
      }
      const body = (await res.json()) as PublicLeaderboard;
      if (stopped) return;
      callbacks.onBoard(body);
      callbacks.onError(null);
    } catch (err) {
      if (stopped || (err as Error).name === 'AbortError') return;
      callbacks.onError('The scores could not be loaded.');
    }
  };

  const stopPoll = () => {
    if (pollTimer != null) {
      window.clearInterval(pollTimer);
      pollTimer = null;
    }
  };

  /** Stop everything for good: no more polls, no reconnects. */
  const shutdown = () => {
    stopped = true;
    stopPoll();
    if (reconnectTimer != null) window.clearTimeout(reconnectTimer);
    reconnectTimer = null;
    socket?.close();
  };

  const startPoll = () => {
    if (stopped) return;
    if (pollTimer != null || !shouldPollLeaderboard('polling')) return;
    void pull();
    pollTimer = window.setInterval(() => void pull(), SCORING_POLL_MS);
  };

  const connect = () => {
    if (stopped) return;
    const url = scoringWebSocketUrl(
      apiUrl(`/api/public/shooting/${encodeURIComponent(slug)}/live`),
    );
    socket = new WebSocket(url);
    socket.onopen = () => {
      attempt = 0;
      callbacks.onConnection('open');
      stopPoll();
    };
    socket.onmessage = (event) => {
      try {
        const message = JSON.parse(String(event.data)) as {
          type?: string;
          leaderboard?: PublicLeaderboard | null;
        };
        if (
          !stopped &&
          message.type === 'leaderboard' &&
          message.leaderboard?.ok
        ) {
          callbacks.onBoard(message.leaderboard);
          callbacks.onError(null);
        }
      } catch {
        // Ignore a malformed push and keep the last good board.
      }
    };
    socket.onerror = () => {
      socket?.close();
    };
    socket.onclose = () => {
      if (stopped) return;
      callbacks.onConnection('polling');
      startPoll();
      const delay = Math.min(8000, 1000 * 2 ** attempt);
      attempt += 1;
      reconnectTimer = window.setTimeout(connect, delay);
    };
  };

  callbacks.onConnection('connecting');
  const startLive = () => {
    if (stopped) return;
    void pull();
    connect();
  };
  if (staticFiles.has(`/public/results/${slug}.json`)) {
    void (async () => {
      try {
        const response = await fetch(
          `/results/${encodeURIComponent(slug)}.json`,
          { credentials: 'omit', signal: controller.signal },
        );
        if (response.ok) {
          const snapshot: unknown = await response.json();
          if (isFinalSnapshot(snapshot, slug)) {
            if (!stopped) {
              callbacks.onBoard(snapshot);
              callbacks.onError(null);
            }
            return;
          }
        }
      } catch {
        /* Missing, invalid or unavailable static files retain the existing live fallback. */
      }
      startLive();
    })();
  } else startLive();

  return () => {
    shutdown();
    controller.abort();
  };
}
